import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWebSocket } from '../context/WebSocket/WebSocketContext';
import { WEB_SOCKET_CHANNELS } from '../utils';
import { insertSummaries } from '../services/threatExplainPersistence';
import { removePendingAiSummary } from '../store/pendingAiSummaries/slice';
import { findMatchingPendingSummary } from '../store/pendingAiSummaries/match';
import { PendingAiSummaryEntry } from '../store/pendingAiSummaries/types';
import { ThreatStatusUpdatePayload, WebSocketMessage } from '../helpers/types';

// Persists the batch AI summary to its threat once the threat exists. Bulk
// creation (bulk_home, bulk_fn) is async: POST v2/assets/threats returns no
// threat_id, so the summary is queued (pendingAiSummaries) at submit and
// written here when the "created" event arrives over the threat_status_update
// WebSocket.
export function useAiSummaryFlusher(): void {
  const dispatch = useDispatch();
  const { subscribe } = useWebSocket();

  const entries = useSelector(
    (state: any) =>
      (state.pendingAiSummaries?.entries ?? []) as PendingAiSummaryEntry[],
  );
  // Keep the latest entries in a ref so the subscription can stay stable
  // (subscribe/unsubscribe once) instead of churning on every queue change.
  const entriesRef = useRef<PendingAiSummaryEntry[]>(entries);
  entriesRef.current = entries;

  useEffect(() => {
    const handler = (
      wsResponse: WebSocketMessage<ThreatStatusUpdatePayload>,
    ) => {
      const data = wsResponse?.data;
      if (!data || data.error) return;
      if (data.operation !== 'created' || !data.threat_id) return;

      const match = findMatchingPendingSummary(entriesRef.current, {
        threatName: data.threat_name,
        companyId: data.company_id,
      });
      if (!match) return;

      // Summaries are keyed by the full (threat, asset, company) tuple -
      // without all three the row can't be written.
      const protectedAssetId =
        data.protected_asset_id ?? match.protectedAssetId;
      const companyId = data.company_id ?? match.companyId;
      if (protectedAssetId == null || companyId == null) {
        console.error(
          'Cannot persist AI summary: missing asset/company id for',
          data.threat_name,
        );
        return;
      }

      insertSummaries(
        [
          {
            threat_id: data.threat_id,
            protected_asset_id: protectedAssetId,
            company_id: companyId,
          },
        ],
        match.summary,
      )
        .then(() =>
          dispatch(
            removePendingAiSummary({
              threatName: match.threatName,
              companyId: match.companyId,
            }),
          ),
        )
        .catch(err =>
          console.error(
            'Failed to persist AI summary on threat creation:',
            err,
          ),
        );
    };

    const unsubscribe = subscribe(
      WEB_SOCKET_CHANNELS.THREAT_STATUS_UPDATE,
      handler,
    );
    return unsubscribe;
  }, [subscribe, dispatch]);
}
